import { useEffect, useRef, useState } from "react";
import { Loader2, Move, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/i18n";
import { api, fileUrl } from "@/lib/api";

const clamp = (v) => Math.min(1, Math.max(0, v));
const isHorizontal = (a) => Math.abs(a.x2 - a.x1) >= Math.abs(a.y2 - a.y1);

// Drag the blue lines (ends or middle) over the infographic, then re-draw it server-side. No AI, no credit.
export default function ArrowEditor(props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="rounded-full border border-[var(--line)] px-4 py-2 text-sm text-neutral-200 flex items-center gap-2 hover:border-emerald-600" data-testid="arrow-editor-open-btn">
        <Move size={15} />{t("tool.editArrows")}
      </button>
    );
  }
  return <Editor {...props} onClose={() => setOpen(false)} />;
}

function Editor({ project, result, onSaved, onClose }) {
  const { t, errMsg } = useI18n();
  const [arrows, setArrows] = useState(result.arrows);
  const [lock, setLock] = useState(true);
  const [busy, setBusy] = useState(false);
  const svg = useRef(null);
  const drag = useRef(null);
  useEffect(() => setArrows(result.arrows), [result.id, result.arrows]);
  const W = result.width, H = result.height;

  const point = (e) => {
    const r = svg.current.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  };
  const start = (i, part) => (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { i, part, from: point(e), orig: { ...arrows[i] } };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = point(e), o = d.orig, hz = isHorizontal(o);
    let n = { ...o };
    if (d.part === "mid") {
      const dx = p.x - d.from.x, dy = p.y - d.from.y;
      n = { ...o, x1: clamp(o.x1 + dx), x2: clamp(o.x2 + dx), y1: clamp(o.y1 + dy), y2: clamp(o.y2 + dy) };
    } else {
      const k = d.part;
      n[`x${k}`] = p.x;
      n[`y${k}`] = p.y;
      if (lock && hz) { n.y1 = p.y; n.y2 = p.y; n[`x${k}`] = p.x; }
      if (lock && !hz) { n.x1 = p.x; n.x2 = p.x; n[`y${k}`] = p.y; }
    }
    setArrows((a) => a.map((x, j) => (j === d.i ? n : x)));
  };
  const end = () => { drag.current = null; };

  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/results/${result.id}/arrows`, {
        arrows: arrows.map(({ key, x1, y1, x2, y2 }) => ({ key, x1, y1, x2, y2 })),
      });
      toast.success(t("tool.arrowsSaved"));
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const hr = Math.max(W, H) * 0.014;
  return (
    <div className="panel p-4 space-y-3" data-testid="arrow-editor">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium flex items-center gap-2 mr-auto"><Move size={15} />{t("tool.editArrows")}</p>
        <button onClick={onClose} className="text-xs text-neutral-400 hover:text-white" data-testid="arrow-editor-close-btn">{t("common.close")}</button>
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-300" data-testid="arrow-legend">
        {arrows.map((a) => <li key={a.key} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-600" />{a.label}</li>)}
      </ul>
      <p className="text-xs text-neutral-400">{t("tool.arrowsHint")}</p>
      <div className="relative light-panel overflow-hidden select-none touch-none" style={{ background: result.bg_hex || "#FFFFFF", aspectRatio: `${W} / ${H}` }}>
        <img src={fileUrl(result.master_file_id)} alt="" className="absolute inset-0 w-full h-full pointer-events-none" draggable={false} data-testid="arrow-editor-image" />
        <svg ref={svg} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full"
          onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} data-testid="arrow-editor-canvas">
          {arrows.map((a, i) => {
            const x1 = a.x1 * W, y1 = a.y1 * H, x2 = a.x2 * W, y2 = a.y2 * H;
            return (
              <g key={a.key} data-testid={`arrow-${a.key}`}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#2563EB" strokeWidth={hr * 0.35} strokeDasharray={`${hr} ${hr * 0.6}`} />
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={hr * 2.2} className="cursor-move" onPointerDown={start(i, "mid")} data-testid={`arrow-${a.key}-line`} />
                {[1, 2].map((k) => (
                  <g key={k}>
                    <circle cx={k === 1 ? x1 : x2} cy={k === 1 ? y1 : y2} r={hr} fill="#2563EB" stroke="#fff" strokeWidth={hr * 0.25} className="pointer-events-none" />
                    {/* Large invisible hit area so the handle is easy to grab with a finger */}
                    <circle cx={k === 1 ? x1 : x2} cy={k === 1 ? y1 : y2} r={hr * 3.5} fill="transparent" className="cursor-grab"
                      onPointerDown={start(i, k)} data-testid={`arrow-${a.key}-end-${k}`}><title>{a.label}</title></circle>
                  </g>
                ))}
              </g>
            );
          })}
        </svg>
      </div>
      <label className="flex items-center gap-2 text-xs text-neutral-300">
        <Checkbox checked={lock} onCheckedChange={(c) => setLock(!!c)} data-testid="arrow-lock-axis" />{t("tool.lockAxis")}
      </label>
      <div className="flex flex-wrap gap-2">
        <button onClick={apply} disabled={busy} className="rounded-full border border-emerald-600 text-emerald-400 px-4 py-2 text-sm hover:bg-emerald-600/10 disabled:opacity-60 flex items-center gap-2" data-testid="arrow-apply-btn">
          {busy && <Loader2 size={15} className="animate-spin" />}{t("tool.applyArrows")}
        </button>
        <button onClick={() => setArrows(result.auto_arrows || result.arrows)} className="rounded-full border border-[var(--line)] px-4 py-2 text-sm text-neutral-300 flex items-center gap-2" data-testid="arrow-reset-btn">
          <RotateCcw size={14} />{t("tool.resetArrows")}
        </button>
      </div>
      <p className="text-[11px] text-neutral-500">{t("tool.freeNote")}</p>
    </div>
  );
}
